'use client';

import {
  type RefObject,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';

import { Observable } from 'rxjs';

import {
  releaseMicOnceOnSessionOver,
  snapshotConversationOverText,
} from './project-usability-handsfree-orchestration.helpers';
import { subscribeRecordingStart } from './project-usability-recording-state.helpers';

export function useHandsFreeAudioLevel(
  audioLevelObservable: Observable<number>
): number {
  const audioLevelRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);

  const [audioLevel, setAudioLevel] = useState(0);

  useEffect(() => {
    const subscription = audioLevelObservable.subscribe((level) => {
      audioLevelRef.current = level;

      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => {
          setAudioLevel(audioLevelRef.current);
          rafIdRef.current = null;
        });
      }
    });
    return () => {
      subscription.unsubscribe();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [audioLevelObservable]);

  return audioLevel;
}

interface UseHandsFreeWelcomeDismissReturn {
  showWelcomePrompt: boolean;
  setShowWelcomePrompt: (value: boolean) => void;
}

// Keep welcome modal up until recording starts so it covers the screen/mic permission prompt.
export function useHandsFreeWelcomeDismiss(
  onRecordingStart: () => void
): UseHandsFreeWelcomeDismissReturn {
  const [showWelcomePrompt, setShowWelcomePrompt] = useState(true);

  const onRecordingStartEvent = useEffectEvent(onRecordingStart);

  useEffect(
    () =>
      subscribeRecordingStart(() => {
        setShowWelcomePrompt(false);
        onRecordingStartEvent();
      }),
    []
  );

  return { showWelcomePrompt, setShowWelcomePrompt };
}

// Release the STT mic stream on session end so the tab's mic indicator clears on the Thank You screen.
export function useHandsFreeStopListeningOnSessionOver(
  isSessionOver: boolean,
  stopListening: (reset?: boolean) => Promise<void>
): void {
  const hasStoppedListeningRef = useRef(false);

  useEffect(
    () =>
      releaseMicOnceOnSessionOver({
        isSessionOver,
        hasStoppedListeningRef,
        stopListening,
      }),
    [isSessionOver, stopListening]
  );
}

export function useHandsFreeConversationOverText(
  isConversationOver: boolean,
  conversationOverRef: RefObject<string | null>
): string | null {
  const [conversationOverText, setConversationOverText] = useState<
    string | null
  >(null);

  useEffect(
    () =>
      snapshotConversationOverText({
        isConversationOver,
        conversationOverRef,
        setConversationOverText,
      }),
    [isConversationOver, conversationOverRef]
  );

  return conversationOverText;
}
